import { actions as verificationActions } from 'ducks/deviceVerifications';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { selectVerificationProfile } from 'selectors/devices';
import { selectReadingsByVerification } from 'selectors/deviceVerificationReadings';
import { selectVerificationsByDevice } from 'selectors/deviceVerifications';
import { selectMeasurementsByDevice } from 'selectors/measurements';
import { selectReadingKindNamesForDevice, selectReadingsByDeviceIdMap, selectReadingsForDevice } from 'selectors/readings';
import { selectUnitSymbolsByReadingKindId } from 'selectors/units';
import { stateForStatus } from './utils';

// Everything the two verification screens do identically: store plumbing, the selected
// verification, the final report and the create/select/delete/conclude handlers.
// resetForm clears whatever form state the screen keeps for a reading in progress.
const useVerificationScreen = (device, resetForm) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const loading = useSelector(state => state.logs.loading);
    const realtime = useSelector(state => state.logs.realtime);
    const measurements = useSelector(useMemo(() => selectMeasurementsByDevice(device.id), [device.id]));
    const readings = useSelector(useMemo(() => selectReadingsForDevice(device), [device]));
    const readingsById = useSelector(useMemo(() => selectReadingsByDeviceIdMap(device), [device]));
    const readingKindNames = useSelector(useMemo(() => selectReadingKindNamesForDevice(device), [device]));
    const unitSymbols = useSelector(selectUnitSymbolsByReadingKindId);
    const verifications = useSelector(useMemo(() => selectVerificationsByDevice(device.id), [device.id]));
    const verificationsLoaded = useSelector(state => state.deviceVerifications.loaded);
    const profile = useSelector(useMemo(() => selectVerificationProfile(device.id), [device.id]));
    const submitting = useSelector(state => state.deviceVerifications.loading || state.deviceVerifications.submitting || state.deviceVerificationReadings.submitting);

    const [verificationsIndex, setVerificationsIndex] = useState(null);
    const currentVerification = verificationsIndex !== null ? verifications[verificationsIndex] : undefined;
    const verificationNumber = verificationsIndex !== null ? verificationsIndex + 1 : verifications.length + 1;
    const verificationReadings = useSelector(useMemo(() => selectReadingsByVerification(currentVerification?.id), [currentVerification?.id]));
    const technicianValidated = verificationReadings.length > 0;
    const finalValidated = currentVerification?.state === 'ok' || currentVerification?.state === 'not_ok';

    const [drawerOpen, setDrawerOpen] = useState(false);
    const [finalStatus, setFinalStatus] = useState('');
    const [finalComments, setFinalComments] = useState('');
    const [image, setImage] = useState(null);
    const [showCameraModal, setShowCameraModal] = useState(false);

    const restoreFinalFields = verification => {
        setFinalStatus(verification?.final_status === 'no_status' ? '' : (verification?.final_status || ''));
        setFinalComments(verification?.final_comments || '');
    };

    useEffect(() => {
        restoreFinalFields(currentVerification);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentVerification?.id, currentVerification?.state, currentVerification?.final_status]);

    const pendingNewRef = useRef(false);
    useEffect(() => {
        if (!pendingNewRef.current || verifications.length === 0) {
            return;
        }
        pendingNewRef.current = false;
        setVerificationsIndex(verifications.length - 1);
    }, [verifications.length]);

    const initializedRef = useRef(false);
    useEffect(() => {
        if (initializedRef.current || !verificationsLoaded) {
            return;
        }
        initializedRef.current = true;
        if (verifications.length > 0) {
            setVerificationsIndex(verifications.length - 1);
            if (location.state?.openVerificationDrawer) {
                setDrawerOpen(true);
            }
        }
    }, [location.state?.openVerificationDrawer, verifications.length, verificationsLoaded]);

    const handleClean = () => {
        pendingNewRef.current = true;
        dispatch(verificationActions.create({ device_id: device.id }, () => {}));
        setDrawerOpen(true);
        setVerificationsIndex(null);
        setFinalStatus('');
        setFinalComments('');
        setImage(null);
        resetForm();
    };

    // The eager restore covers re-selecting the SAME verification, where the effect
    // above sees no change — without it an abandoned draft survives the reopen.
    const handleSelect = index => {
        setVerificationsIndex(index);
        restoreFinalFields(verifications[index]);
        setDrawerOpen(true);
    };

    const handleDelete = () => {
        dispatch(verificationActions.remove(currentVerification.id));
        setDrawerOpen(false);
        setVerificationsIndex(null);
    };

    const handleFinalValidation = () => {
        dispatch(verificationActions.update(currentVerification.id, {
            final_comments: finalComments,
            final_status: finalStatus,
            state: stateForStatus(finalStatus)
        }, () => {}));
    };

    const handleSaveImage = imageDataUrl => {
        setImage(imageDataUrl);
        setShowCameraModal(false);
    };

    return {
        currentVerification, dispatch, drawerOpen, finalComments, finalStatus, finalValidated, image, loading, measurements, profile, readingKindNames, readings,
        readingsById, realtime, showCameraModal, submitting, technicianValidated, unitSymbols, verificationNumber, verificationReadings, verificationsIndex,
        handleClean, handleDelete, handleFinalValidation, handleSaveImage, handleSelect, restoreFinalFields, setDrawerOpen, setFinalComments, setFinalStatus, setImage, setShowCameraModal
    };
};

export default useVerificationScreen;
